import { useLayoutEffect, useRef, type ComponentType, type ReactNode } from 'react';
import { LayoutGrid } from 'lucide-react';
import { cn } from '@reflex/ui/lib/utils';

export interface MobileTabItem {
  value: string;
  label: string;
  icon?: ComponentType<{ className?: string }>;
}

interface MobileViewStripProps {
  activeView: string;
  items: MobileTabItem[];
  onViewChange: (view: string) => void;
  /** Fixed slot on the right (typically the actions sheet trigger). */
  trailing?: ReactNode;
  className?: string;
}

const ACTIVE_ITEM_MARGIN_PX = 12;

/**
 * Scroll only this strip. `scrollIntoView` would also move the page, which
 * fights the chat scroller underneath.
 */
function revealActiveItem(item: HTMLElement, scroller: HTMLElement) {
  const itemRect = item.getBoundingClientRect();
  const scrollerRect = scroller.getBoundingClientRect();
  const overflowLeft = scrollerRect.left + ACTIVE_ITEM_MARGIN_PX - itemRect.left;
  const overflowRight = itemRect.right - (scrollerRect.right - ACTIVE_ITEM_MARGIN_PX);
  if (overflowLeft > 0) scroller.scrollLeft -= overflowLeft;
  else if (overflowRight > 0) scroller.scrollLeft += overflowRight;
}

/**
 * Mobile view switcher: a horizontally scrollable tab strip of pinned views,
 * with an optional trailing actions trigger that stays pinned to the right edge.
 */
export function MobileViewStrip({
  activeView,
  items,
  onViewChange,
  trailing,
  className,
}: MobileViewStripProps) {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const activeRef = useRef<HTMLButtonElement>(null);

  useLayoutEffect(() => {
    const item = activeRef.current;
    const scroller = scrollerRef.current;
    if (!item || !scroller) return;
    revealActiveItem(item, scroller);
  }, [activeView, items]);

  return (
    <div
      className={cn('flex h-10 items-center border-b border-border/40 bg-muted/20', className)}
      data-testid="mobile-view-strip"
    >
      <div className="relative min-w-0 flex-1">
        <div
          ref={scrollerRef}
          role="tablist"
          aria-label="Views"
          className={cn(
            'flex h-10 items-stretch overflow-x-auto overscroll-x-contain px-1',
            '[scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden',
          )}
        >
          {items.map((item) => {
            const Icon = item.icon ?? LayoutGrid;
            const isActive = item.value === activeView;
            return (
              <button
                key={item.value}
                ref={isActive ? activeRef : undefined}
                type="button"
                role="tab"
                aria-selected={isActive}
                data-testid={`mobile-view-${item.value}`}
                className={cn(
                  'relative inline-flex h-full shrink-0 items-center gap-1.5 rounded-none px-2.5',
                  'text-xs font-medium tracking-tight whitespace-nowrap shadow-none',
                  'transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  isActive
                    ? 'bg-muted/40 text-foreground after:absolute after:inset-x-0 after:bottom-0 after:h-0.5 after:bg-ring'
                    : 'text-muted-foreground hover:bg-muted/30 hover:text-foreground',
                )}
                onClick={() => onViewChange(item.value)}
              >
                <Icon className="size-3.5 shrink-0" aria-hidden="true" />
                {item.label}
              </button>
            );
          })}
        </div>
        <div
          className="pointer-events-none absolute inset-y-0 right-0 w-6 bg-gradient-to-l from-background"
          aria-hidden="true"
        />
      </div>
      {trailing ? (
        <div
          className="flex h-10 shrink-0 items-stretch border-l border-border/40"
          data-testid="mobile-view-strip-trailing"
        >
          {trailing}
        </div>
      ) : null}
    </div>
  );
}
